import { PropertyFilterProps } from '@amzn/awsui-components-react-v3/polaris';
import { SELECTOR_CONSTANTS, URL_PARAMS } from '../constants';
import { DomainCollection } from '@amzn/swo-unified-dashboard-service-lambda-js-client/lib/smswounifieddashboardservicelambda';
import { LoadItemsDetail } from '@amzn/awsui-components-react-v3/polaris/property-filter/interfaces';

export interface HandleChangeParams {
  detail: PropertyFilterProps.Query;
  history: {
    push: (path: string) => void;
  };
  pathname: string;
}

export interface HandleLoadItemsParams {
  detail: LoadItemsDetail;
  setSearchQuery: (query: { text: string; queryProp: string }) => void;
}

/**
 * Handles property filter changes and updates URL parameters
 * @param params - Object containing detail, history, and pathname
 */
export const handleWebsiteFilterChange = ({ 
  detail, 
  history, 
  pathname 
}: HandleChangeParams): void => {
  const websiteToken = detail.tokens[detail.tokens.length - 1];
  
  if (websiteToken) {
    const searchProp = websiteToken.propertyKey;
    const lastColonIndex = websiteToken.value.lastIndexOf(':');
    const [website, websiteId] = lastColonIndex === -1 
      ? [websiteToken.value, '']
      : [
          websiteToken.value.substring(0, lastColonIndex), 
          websiteToken.value.substring(lastColonIndex + 1)
        ];
    
    const params = new URLSearchParams();
    params.set(URL_PARAMS.WEBSITE, website);
    
    if (websiteId) {
      params.set(URL_PARAMS.WEBSITE_ID, websiteId);
    }
    
    if (searchProp) {
      params.set(URL_PARAMS.SEARCH_PROP, searchProp);
    }
    
    history.push(`${pathname}?${params.toString()}`);
  }else{
    history.push(`${pathname}`);
  }
};

/**
 * Handles loading items for property filter
 * @param params - Object containing detail and setSearchQuery callback
 */
export const handleWebsiteFilterLoadItems = ({ 
  detail, 
  setSearchQuery 
}: HandleLoadItemsParams): void => {
  setSearchQuery({
    text: detail.filteringText,
    queryProp: (detail && detail.filteringProperty && detail.filteringProperty.key) || ''
  });
};

/**
 * Parses URL parameters and returns search query state
 * @param getParamValue - Function to get URL parameter values
 * @returns Object containing text and queryProp for search
 */
export const parseUrlParamsToSearchQuery = (
  getParamValue: (param: string) => string | null
): { text: string; queryProp: string } => {
  const currentWebsite = getParamValue(URL_PARAMS.WEBSITE) || '';
  const currentWebsiteId = getParamValue(URL_PARAMS.WEBSITE_ID);
  const currentSearchProperty = getParamValue(URL_PARAMS.SEARCH_PROP);
  
  const isWebsiteIdSearch = 
    currentSearchProperty === SELECTOR_CONSTANTS.PROPERTY_KEYS.WEBSITE_ID && 
    currentWebsiteId;
  
  const newText = isWebsiteIdSearch ? currentWebsiteId : currentWebsite;
  
  return {
    text: newText || '',
    queryProp: currentSearchProperty || SELECTOR_CONSTANTS.PROPERTY_KEYS.DOMAIN
  };
};

/**
 * Creates filter tokens from URL parameters
 * @param getParamValue - Function to get URL parameter values
 * @returns PropertyFilterProps.Query object with tokens
 */
export const createFilterTokensFromUrl = (
  getParamValue: (param: string) => string | null
): PropertyFilterProps.Query => {
  const currentWebsite = getParamValue(URL_PARAMS.WEBSITE) || '';
  const currentWebsiteId = getParamValue(URL_PARAMS.WEBSITE_ID);
  const currentSearchProperty = getParamValue(URL_PARAMS.SEARCH_PROP);
  
  const value = currentWebsiteId 
    ? `${currentWebsite}:${currentWebsiteId}` 
    : `${currentWebsite}`;
  
  return {
    tokens: value ? [{
      propertyKey: currentSearchProperty || SELECTOR_CONSTANTS.PROPERTY_KEYS.DOMAIN,
      value,
      operator: SELECTOR_CONSTANTS.OPERATORS.EQUALS
    }] : [],
    operation: SELECTOR_CONSTANTS.FILTER_OPERATION
  };
};

/**
 * Generates filtering options from domain collection
 * @param domainCollection - Array of domain objects
 * @param queryProp - Current query property key
 * @returns Array of filtering options for PropertyFilter
 */
export const generateFilteringOptions = (
  domainCollection: DomainCollection | undefined,
  queryProp: string
): Array<{ propertyKey: string; value: string }> => {
  if (!domainCollection) {
    return [];
  }
  
  return domainCollection.map(domain => ({
    propertyKey: queryProp,
    value: domain.websiteId 
      ? `${domain.domain}:${domain.websiteId}` 
      : `${domain.domain}`
  }));
};
